/**
 * How far out from the wall a wall prop (wall art, a towel bar) must be held
 * so that nothing of it is inside the product on the wall: SlatWall stands
 * further off the wall plane than Wall&CeilingBoard, and a picture hung at
 * the wall plane across the two has the slats through its lower half. The
 * depths are the products' own extrusion profiles (the same geometry the
 * view builds and the render reads), so the prop clears what is really there.
 */

import type { Section, SectionProductType } from '../../../types/sections';
import { SLATWALL_PROFILE } from './profiles/slatwall';
import { WCB_PROFILE } from './profiles/wcb';

/** A hair's clearance off the product's face (feet): a quarter inch. */
export const STANDOFF_GAP = 0.25 / 12;

/** How far a product stands off the wall plane (feet). */
export function productDepth(type: SectionProductType): number {
  return type === 'SlatWall' ? SLATWALL_PROFILE.depthSize : WCB_PROFILE.depthSize;
}

export interface WallRect { x0: number; x1: number; y0: number; y1: number }

/** The deepest product under a rectangle of the wall (surface feet), 0 over bare wall. */
export function deepestUnder(sections: readonly Pick<Section, 'productType' | 'x' | 'y' | 'width' | 'height'>[], rect: WallRect): number {
  let depth = 0;
  for (const s of sections) {
    if (s.x >= rect.x1 || s.x + s.width <= rect.x0 || s.y >= rect.y1 || s.y + s.height <= rect.y0) continue;
    depth = Math.max(depth, productDepth(s.productType));
  }
  return depth;
}

/**
 * The hold point's distance from the wall so the prop's back clears `depth`
 * by the gap: the frame's own when it already does, further out when not.
 * `d` is the prop's depth and `bodyOut` how far its body's centre is off the
 * hold point into the room (a model placed off the hold; 0 otherwise).
 */
export function holdZClear(holdZ: number, d: number, bodyOut: number, depth: number, gap = STANDOFF_GAP): number {
  if (depth <= 0) return holdZ;
  const back = holdZ + bodyOut - d / 2; // the back of the prop, off the wall plane
  const need = depth + gap;
  return back >= need ? holdZ : holdZ + (need - back);
}
